import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { Grid } from '@mui/material';
import { Policy, WarningRounded } from '@mui/icons-material';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import RcaPolicyDataComponent from './RcaPolicyDataComponent';
import CascoPolicyDataComponent from './CascoPolicyDataComponent';
import PolicySummary from './PolicySummary';
import { get } from '@totalsoft/change-tracking-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { CardActionsContainer } from 'components/common/CardStyles';
import { PolicyWarningContainer } from '../../PolicyStyles';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const PolicyDataCard = ({
  loading,
  policyDataLens,
  paymentTypes,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  validation,
  onSave,
  onGenerate,
  onDownload,
  saving,
  generating,
  saveDisabled,
  generateDisabled,
  generateHidden,
  downloadDisabled,
  readOnly
}) => {
  const { t } = useTranslation();
  const { globalReadOnly: contextGlobalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditPolicyDataCard = useAuthorization(AuthorizationCodes.hasRightToEditPolicyDataCard, documentTypeId, documentStateId);

  const generationLabels = useMemo(
    () =>
      insuranceTypeCode === rcaInsuranceTypeCode
        ? { ready: t('Offer.Edit.PolicyData.GeneratePolicy'), progress: t('Offer.Edit.PolicyData.GeneratingPolicy') }
        : { ready: t('General.Generate'), progress: t('General.Generating') },
    [insuranceTypeCode, t, rcaInsuranceTypeCode]
  );

  const globalReadOnly = useCallback(
    (controlName, hasAuthorization) =>
      contextGlobalReadOnly({
        controlName,
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true },
        hasAuthorization
      }) || readOnly,
    [contextGlobalReadOnly, readOnly]
  );

  return (
    <CollapseCard
      icon={Policy}
      title={t('Offer.Edit.PolicyData.Title')}
      defaultExpanded={true}
      subheader={<PolicySummary policyData={policyDataLens |> get} loading={loading} />}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container spacing={2} px={1}>
        {loading ? (
          <FakeTextWrapper sx={{ ml: 2 }} />
        ) : (
          <>
            {insuranceTypeCode === rcaInsuranceTypeCode && (
              <RcaPolicyDataComponent
                policyDataLens={policyDataLens}
                paymentTypes={paymentTypes}
                validation={validation}
                readOnly={globalReadOnly}
              />
            )}
            {insuranceTypeCode === cascoInsuranceTypeCode && (
              <CascoPolicyDataComponent
                policyDataLens={policyDataLens}
                paymentTypes={paymentTypes}
                validation={validation}
                readOnly={globalReadOnly}
              />
            )}
          </>
        )}
        <Grid item container xs={12} alignItems={'center'}>
          {(policyDataLens.negotiatedQuotation |> get) && insuranceTypeCode === rcaInsuranceTypeCode ? (
            <PolicyWarningContainer>
              <WarningRounded />
              <span>{t('Validations.Generic.NegotiatedQuotationSelectedWarning')}</span>
            </PolicyWarningContainer>
          ) : (
            <CardActionsContainer>
              <Button
                color='primary'
                onClick={onSave}
                disabled={saveDisabled || globalReadOnly('PolicyData', hasRightToEditPolicyDataCard)}
                id='Policy_Save'
              >
                {saving ? t('General.Saving') : t('General.Save')}
              </Button>
              <Button color='primary' onClick={onDownload} disabled={downloadDisabled} right={true} id='Policy_Download'>
                {t('Tooltips.Download')}
              </Button>
              {!generateHidden() && (
                <Button
                  color='primary'
                  onClick={onGenerate}
                  disabled={generateDisabled || globalReadOnly('PolicyData', hasRightToEditPolicyDataCard)}
                  right={true}
                  id='Policy_Generate'
                >
                  {generating ? generationLabels.progress : generationLabels.ready}
                </Button>
              )}
            </CardActionsContainer>
          )}
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

PolicyDataCard.propTypes = {
  loading: PropTypes.bool.isRequired,
  policyDataLens: PropTypes.object.isRequired,
  paymentTypes: PropTypes.array,
  insuranceTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  validation: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  generating: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  generateDisabled: PropTypes.bool.isRequired,
  generateHidden: PropTypes.func,
  downloadDisabled: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired
};

export default PolicyDataCard;
